/* global axios */
import ApiClient from '../ApiClient';

class ConversationApi extends ApiClient {
  constructor() {
    super('conversations', { accountScoped: true });
  }

  get(
    {
      inboxId,
      status,
      assigneeType,
      page,
      labels,
      label_id,
      label_source_id,
      conversationType,
      sortBy,
      updatedWithin,
      assignee_id,
      per_page,
      include_labeled,
      contact_id,
      ai_replied,
      unread_first,
      has_priority,
    },
    signal
  ) {
    // Costruisci l'oggetto params includendo solo i valori non-di-default
    const params = {
      status,
      labels,
      label_id,
      label_source_id,
      preview: true,
    };

    // Includi inbox_id solo se è valorizzato e diverso da 0
    // Evita di passare "0" o null/undefined che indicano la vista "Tutte le conversazioni"
    if (inboxId !== undefined && inboxId !== null && String(inboxId) !== '0') {
      params.inbox_id = inboxId;
    }

    // Campi facoltativi – aggiunti solo se valorizzati e utili
    if (assignee_id) params.assignee_id = assignee_id;
    if (page && page !== 1) params.page = page; // 1 è già default lato BE
    if (per_page) params.per_page = per_page;
    if (conversationType) params.conversation_type = conversationType;
    if (updatedWithin) params.updated_within = updatedWithin;
    if (contact_id) params.contact_id = contact_id;
    if (ai_replied) params.ai_replied = ai_replied;
    if (unread_first) params.unread_first = 'true';
    if (has_priority) params.has_priority = 'true';

    // Non inviare i valori di default
    if (assigneeType && assigneeType !== 'all')
      params.assignee_type = assigneeType;
    if (sortBy && sortBy !== 'last_activity_at_desc') params.sort_by = sortBy;
    if (include_labeled) params.include_labeled = include_labeled; // solo se true

    return axios.get(this.url, {
      params,
      signal,
    });
  }

  filter(payload) {
    return axios
      .post(`${this.url}/filter`, payload.queryData, {
        params: { page: payload.page },
      })
      .then((response) => {
        return response;
      })
      .catch(() => {
        // Restituiamo un oggetto con struttura valida ma vuoto, incluso payload per evitare errori nello store
        return {
          data: {
            payload: [],
            meta: {
              mine_count: 0,
              all_count: 0,
              unassigned_count: 0,
              unread_count: 0,
              unread_count_by_inbox: {},
              inboxes: {},
            },
          },
        };
      });
  }

  createConversation(payload) {
    return axios.post(this.url, payload);
  }

  search({ q }) {
    return axios.get(`${this.url}/search`, {
      params: {
        q,
        page: 1,
      },
    });
  }

  toggleStatus({ conversationId, status, snoozedUntil = null }) {
    return axios.post(`${this.url}/${conversationId}/toggle_status`, {
      status,
      snoozed_until: snoozedUntil,
    });
  }

  setArchived({ conversationId, status, expectedIncomingId }) {
    return axios.post(`${this.url}/${conversationId}/archive`, {
      status,
      expected_incoming_id: expectedIncomingId,
    });
  }

  togglePriority({ conversationId, priority }) {
    return axios.post(`${this.url}/${conversationId}/toggle_priority`, {
      priority,
    });
  }

  assignAgent({ conversationId, agentId }) {
    return axios.post(
      `${this.url}/${conversationId}/assignments?assignee_id=${agentId}`,
      {}
    );
  }

  markMessageRead({ id }) {
    return axios.post(`${this.url}/${id}/update_last_seen`);
  }

  markMessagesUnread({ id }) {
    return axios.post(`${this.url}/${id}/unread`);
  }

  toggleStarred({ conversationId, starred }) {
    return axios.post(`${this.url}/${conversationId}/toggle_starred`, {
      starred,
    });
  }

  assignGmailLabels({
    conversationId,
    targetLabelId,
    targetLabelTitle,
    inboxId,
    currentLabelId = null,
  }) {
    const payload = {
      target_label_id: targetLabelId,
      target_label_title: targetLabelTitle,
      inbox_id: inboxId,
    };

    if (currentLabelId) {
      payload.current_label_id = currentLabelId;
    }

    return axios.post(
      `${this.url}/${conversationId}/assign_gmail_labels`,
      payload
    );
  }

  assignMicrosoftFolders({
    conversationId,
    targetFolderId,
    targetFolderTitle,
    inboxId,
    currentFolderId = null,
  }) {
    const payload = {
      target_folder_id: targetFolderId,
      target_folder_title: targetFolderTitle,
      inbox_id: inboxId,
    };

    if (currentFolderId) {
      payload.current_folder_id = currentFolderId;
    }

    return axios.post(
      `${this.url}/${conversationId}/assign_microsoft_folders`,
      payload
    );
  }

  assignImapFolders({
    conversationId,
    targetFolderId,
    targetFolderTitle,
    inboxId,
    currentFolderId = null,
  }) {
    const payload = {
      target_folder_id: targetFolderId,
      target_folder_title: targetFolderTitle,
      inbox_id: inboxId,
    };

    if (currentFolderId) {
      payload.current_folder_id = currentFolderId;
    }

    return axios.post(
      `${this.url}/${conversationId}/assign_imap_folders`,
      payload
    );
  }

  toggleTyping({ conversationId, status, isPrivate }) {
    return axios.post(`${this.url}/${conversationId}/toggle_typing_status`, {
      typing_status: status,
      is_private: isPrivate,
    });
  }

  mute(conversationId) {
    return axios.post(`${this.url}/${conversationId}/mute`);
  }

  unmute(conversationId) {
    return axios.post(`${this.url}/${conversationId}/unmute`);
  }

  // Concierge: "Ridai la parola a Otello" toglie la pausa dell'AI che la
  // risposta dello staff ha messo sulla conversazione.
  resumeConciergeAi(conversationId) {
    return axios.post(`${this.url}/${conversationId}/concierge_ai_resume`);
  }

  meta({
    inboxId,
    status,
    assigneeType,
    labels,
    conversationType,
    label_id,
    label_source_id,
    include_labeled,
    assignee_id,
    contact_id,
  }) {
    // Allinea la costruzione dei params a `get` per evitare di inviare inbox_id=0/null
    const params = {};
    if (inboxId !== undefined && inboxId !== null && String(inboxId) !== '0') {
      params.inbox_id = inboxId;
    }
    if (status) params.status = status;
    if (assigneeType && assigneeType !== 'all')
      params.assignee_type = assigneeType;
    if (labels) params.labels = labels;
    if (conversationType) params.conversation_type = conversationType;
    if (label_id) params.label_id = label_id;
    if (label_source_id) params.label_source_id = label_source_id;
    if (include_labeled) params.include_labeled = include_labeled;
    if (assignee_id) params.assignee_id = assignee_id;
    if (contact_id) params.contact_id = contact_id;

    return axios
      .get(`${this.url}/meta`, { params })
      .then((response) => {
        if (!response.data?.meta) {
          throw new Error('Struttura risposta non valida');
        }
        return response;
      })
      .catch(() => {
        return {
          data: {
            meta: {
              mine_count: 0,
              all_count: 0,
              unassigned_count: 0,
              unread_count: 0,
              unread_count_by_inbox: {},
              inboxes: {},
            },
          },
        };
      });
  }

  sendEmailTranscript({ conversationId, email }) {
    return axios.post(`${this.url}/${conversationId}/transcript`, { email });
  }

  updateCustomAttributes({ conversationId, customAttributes }) {
    return axios.post(`${this.url}/${conversationId}/custom_attributes`, {
      custom_attributes: customAttributes,
    });
  }

  fetchParticipants(conversationId) {
    return axios.get(`${this.url}/${conversationId}/participants`);
  }

  updateParticipants({ conversationId, userIds }) {
    return axios.patch(`${this.url}/${conversationId}/participants`, {
      user_ids: userIds,
    });
  }

  getAllAttachments(conversationId) {
    return axios.get(`${this.url}/${conversationId}/attachments`);
  }

  getLatestId() {
    return axios
      .get(`${this.url}/latest_id`)
      .then((response) => response.data)
      .catch(() => {
        return {
          latest_id: 0,
          account_id: null,
          timestamp: null,
        };
      });
  }
}

export default new ConversationApi();
